import { Component, inject, signal, computed, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { dateFormattingOptions } from '@domain/formatting';
import { LocaleValue, SlaughterhouseItem } from '@hjc/masterdata-types';
import { LocalizationService } from '@services/localization.service';
import { PartnersService } from '@services/partners.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, RowClickedEvent, themeAlpine } from 'ag-grid-community';
import Fuse from 'fuse.js';
import { debounceTime, Subscription } from 'rxjs';
import { PageHeaderComponent } from '@components/page-header/page-header.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'md-pricing-slaughterhouses-page',
  imports: [PageHeaderComponent, MatFormFieldModule, MatInputModule, MatIconModule, AgGridAngular, ReactiveFormsModule, TranslateModule],
  templateUrl: './pricing-slaughterhouses-page.component.html',
  styleUrl: './pricing-slaughterhouses-page.component.scss',
  standalone: true
})
export class PricingSlaughterhousesPageComponent implements OnDestroy {
  // Inject services
  private partnersService = inject(PartnersService);
  private localizationService = inject(LocalizationService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  // State with signals
  isLoading = signal<boolean>(false);
  allSlaughterhouses = signal<SlaughterhouseItem[]>([]);
  displaySlaughterhouses = signal<SlaughterhouseItem[]>([]);
  lang = signal<LocaleValue>('fi');

  // Regular state
  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<SlaughterhouseItem>;
  theme = themeAlpine;

  // Computed column definitions based on language
  columnDefs = computed(() => this.getColumnDefs());

  constructor() {
    // Initialize language from service
    this.lang.set(this.localizationService.currentLang);
    this.init();
  }

  private getColumnDefs(): ColDef<SlaughterhouseItem>[] {
    return [
      { headerName: this.translate.instant('FIELDS.IDENTIFIER'), field: 'id', sortable: true, flex: 1 },
      { headerName: this.translate.instant('INFO.NAME'), field: `name.${this.lang()}`, sortable: true, flex: 1 },
      { headerName: this.translate.instant('FIELDS.STREET_ADDRESS'), field: 'address', sortable: true, flex: 1 },
      { headerName: this.translate.instant('FIELDS.POSTAL_CODE'), field: 'zip', sortable: true, flex: 1 },
      { headerName: this.translate.instant('FIELDS.CITY'), field: 'city', sortable: true, flex: 1 },
      { headerName: this.translate.instant('FIELDS.SYSTEM.CREATED_BY'), valueGetter: p => p.data?.createdBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: this.translate.instant('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      },
      { headerName: this.translate.instant('FIELDS.SYSTEM.UPDATED_BY'), valueGetter: p => p.data?.updatedBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: this.translate.instant('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      }
    ];
  }

  async init() {
    this.isLoading.set(true);

    try {
      const slaughterhouses = (await this.partnersService.getSlaughterhousePartners())?.data || [];
      this.allSlaughterhouses.set(slaughterhouses);
      this.displaySlaughterhouses.set(slaughterhouses);

      this.initFuse();
      this.searchTermSubscription = this.searchInput.valueChanges.pipe(debounceTime(300)).subscribe(term => this.search(term));
    } catch (error) {
      console.error('Failed to load slaughterhouses', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  initFuse() {
    this.fuse = new Fuse<SlaughterhouseItem>(this.allSlaughterhouses(), {
      keys: [
        'id',
        `name.${this.lang()}`,
        'address',
        'postalCode',
        'city',
        'fillingSpots.id',
        `byProducts.name.${this.lang()}`,
        `byProducts.productType.name.${this.lang()}`
      ],
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) {
      return;
    }

    if (!term || term.length === 0) {
      this.displaySlaughterhouses.set(this.allSlaughterhouses());
      return;
    }

    const results = this.fuse.search(term);
    this.displaySlaughterhouses.set(results.map(r => r.item));
  }

  onRowClicked(event: RowClickedEvent) {
    this.router.navigate([event.data.id], { relativeTo: this.activatedRoute });
  }

  ngOnDestroy() {
    this.searchTermSubscription?.unsubscribe();
  }
}
